import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, UserCheck, Network, ShieldCheck, MessageCircle } from "lucide-react";
import { waUrl } from "../lib/api";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9f4ae8e0-d55b-474c-b746-7744a9f344d3/images/8d7c8730f0b1e5c54be501687b0fd28efe70a9f512ec1f1ceb0e7e370f985736.png";

export default function HeroSection() {
    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section className="relative overflow-hidden bg-white" data-testid="hero-section">
            <div className="absolute inset-0 bg-grain opacity-50 pointer-events-none" />
            <div className="max-w-7xl mx-auto px-6 md:px-12 pt-14 pb-20 md:pt-20 md:pb-28 grid md:grid-cols-12 gap-10 md:gap-12 items-center relative">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="md:col-span-7"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0A1A2F]/5 border border-[#0A1A2F]/10 text-xs font-semibold text-[#0A1A2F] mb-6" data-testid="hero-badge">
                        <Sparkles className="w-3.5 h-3.5 text-[#F5A623]" />
                        Gratis Assessment Awal • Dipandu Counselor Senior
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1A2F] leading-[1.05] tracking-tight">
                        Temukan{" "}
                        <span className="relative inline-block">
                            <span className="relative z-10">Kampus Luar Negeri</span>
                            <span className="absolute bottom-1 left-0 right-0 h-3 bg-[#F5A623]/40 -z-0 rounded" />
                        </span>{" "}
                        yang Realistis untuk Profil & Budgetmu.
                    </h1>
                    <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
                        Gantari Edu membantumu memilih negara, jurusan, kampus, estimasi biaya, dan peluang lolos — <strong className="text-[#0A1A2F]">dipercepat teknologi pintar, lalu divalidasi langsung oleh Counselor Senior.</strong>
                    </p>
                    <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <button
                            data-cta="cta_hero_assessment"
                            data-testid="hero-cta-assessment"
                            onClick={() => scrollTo("smart-search")}
                            className="btn-shimmer group inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#0A1A2F] hover:bg-[#D98F1B] rounded-full px-7 py-4 font-bold transition-all shadow-[0_4px_14px_0_rgba(245,166,35,0.39)] hover:shadow-[0_6px_20px_rgba(245,166,35,0.55)]"
                        >
                            Cari Kampus yang Cocok
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <a
                            href={waUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-cta="cta_hero_whatsapp"
                            data-testid="hero-cta-whatsapp"
                            className="inline-flex items-center justify-center gap-2 border-2 border-[#0A1A2F] text-[#0A1A2F] hover:bg-[#0A1A2F] hover:text-white rounded-full px-7 py-4 font-semibold transition-all"
                        >
                            <MessageCircle className="w-4 h-4" /> Konsultasi via WhatsApp
                        </a>
                    </div>
                    <p className="mt-5 text-xs sm:text-[13px] text-slate-500 max-w-xl leading-relaxed">
                        Untuk siswa SMA/SMK, mahasiswa S1, lulusan S1, dan orang tua yang ingin merencanakan studi luar negeri dengan lebih matang.
                    </p>
                    <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
                        <Stat n="1-on-1" l="Guidance" />
                        <Stat n="40+" l="Negara Tujuan" />
                        <Stat n="1.500+" l="Kampus Mitra Global" sub="*Via ekosistem jaringan global terpercaya." />
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7, delay: 0.15 }}
                    className="md:col-span-5 relative"
                >
                    <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                        <img
                            src={HERO_IMG}
                            alt="Kampus internasional"
                            className="w-full h-full object-cover"
                            data-testid="hero-image"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1A2F]/50 via-transparent to-transparent" />
                    </div>
                    {/* 3 floating overlay cards */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        className="absolute -top-3 -left-4 bg-white rounded-2xl shadow-xl border border-slate-100 p-3.5 flex items-center gap-3 max-w-[235px]"
                    >
                        <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <ShieldCheck className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="text-[12px] font-semibold text-[#0A1A2F] leading-tight">Safe · Match · Ambitious<br /><span className="text-slate-500 font-normal text-[11px]">University Options</span></div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.65 }}
                        className="absolute top-1/2 -right-4 bg-white rounded-2xl shadow-xl border border-slate-100 p-3.5 flex items-center gap-3 max-w-[225px]"
                    >
                        <div className="w-9 h-9 rounded-lg bg-[#F5A623]/15 flex items-center justify-center flex-shrink-0">
                            <Network className="w-4 h-4 text-[#F5A623]" />
                        </div>
                        <div className="text-[12px] font-semibold text-[#0A1A2F] leading-tight">Budget & ROI<br /><span className="text-slate-500 font-normal text-[11px]">Simulation</span></div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.8 }}
                        className="absolute -bottom-4 -left-2 bg-white rounded-2xl shadow-xl border border-slate-100 p-3.5 flex items-center gap-3 max-w-[235px]"
                    >
                        <div className="w-9 h-9 rounded-lg bg-[#0A1A2F] flex items-center justify-center flex-shrink-0">
                            <UserCheck className="w-4 h-4 text-[#F5A623]" />
                        </div>
                        <div className="text-[12px] font-semibold text-[#0A1A2F] leading-tight">Senior Counselor<br /><span className="text-slate-500 font-normal text-[11px]">Review & Validation</span></div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}

function Stat({ n, l, sub }) {
    return (
        <div>
            <div className="font-display text-2xl sm:text-3xl font-bold text-[#0A1A2F]">{n}</div>
            <div className="text-xs text-slate-500 mt-1">{l}</div>
            {sub && <div className="text-[9px] text-slate-400 mt-0.5 leading-tight">{sub}</div>}
        </div>
    );
}
